import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead
import { useBooking } from '../../../../contexts/BookingContext';
import PaymentSenseConnectE from '../../../common/PaymentSenseConnectE';
import PaymentSenseWallet from '../../../common/PaymentSenseWallet';
import { venposService } from '../../../../services/venposService';
import {
  isAdultProductCode,
  isChildProductCode,
} from '../membershipUpgradeProductCodes';

// Error handling removed for production security

// ⚡ FEATURE FLAG — match the flag in BookingJourney.js; set true to re-enable the offer
const CHILDREN_RETURN_OFFER_ENABLED = true;

/**
 * Format a Date object as YYYY-MM-DD in local timezone
 * Avoids timezone conversion issues that occur with toISOString()
 */
const formatDateLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const paymentCardShadow = `
  0 1px 0 rgba(255, 255, 255, 0.92) inset,
  0 12px 36px rgba(16, 29, 64, 0.07)
`;

const PaymentContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: clamp(1.35rem, 3.5vw, 1.85rem);
`;

const PageHeaderAccent = styled.div`
  width: 40px;
  height: 3px;
  margin: 0 auto 0.85rem;
  border-radius: 99px;
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.accent},
    ${props => props.theme.colors.primary}
  );
  opacity: 0.88;
`;

const SectionEyebrow = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.42);
  margin-bottom: 0.45rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.45rem, 3.2vw, 1.85rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.55rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const SectionSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.75vw, 0.96rem);
  color: rgba(16, 29, 64, 0.52);
  margin: 0 auto;
  max-width: 34rem;
  line-height: 1.6;
`;

const PaymentSection = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: clamp(1.15rem, 2.8vw, 1.75rem);
  margin-bottom: 1.25rem;
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow: ${paymentCardShadow};
`;

const SectionHeader = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.02rem, 2.1vw, 1.14rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const ErrorMessage = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #b71c1c;
  font-size: 0.82rem;
  margin-top: 0.28rem;
  line-height: 1.35;
`;


const OrderSummary = styled.div`
  background: white;
  border-radius: 16px;
  padding: clamp(1.15rem, 2.8vw, 1.75rem);
  border: 1px solid rgba(157, 109, 73, 0.28);
  margin-bottom: 1.25rem;
  box-shadow: ${paymentCardShadow};
`;

const ItemsSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: clamp(1.05rem, 2.6vw, 1.45rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  margin-bottom: 1rem;
  box-shadow: ${paymentCardShadow};
`;

const ItemsHeader = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.02rem, 2.05vw, 1.12rem);
  margin: 0 0 0.85rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.65rem 0.85rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.85vw, 0.94rem);
  color: rgba(16, 29, 64, 0.88);
  line-height: 1.35;
`;

const ItemQty = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.82rem;
  color: rgba(16, 29, 64, 0.48);
  text-align: right;
`;

const ItemTotal = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 0.92rem;
  text-align: right;
`;

const ModifierInfo = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  color: rgba(16, 29, 64, 0.48);
  margin-top: 0.2rem;
  grid-column: 1 / -1;
`;

const DiscountSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: clamp(1.05rem, 2.6vw, 1.45rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  margin-bottom: 1rem;
  box-shadow: ${paymentCardShadow};
`;

const DiscountHeader = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.02rem, 2.05vw, 1.12rem);
  margin: 0 0 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const DiscountInputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-bottom: 1rem;
`;

const DiscountInput = styled.input`
  flex: 1;
  min-width: 160px;
  padding: 0.65rem 0.85rem;
  border: 1px solid rgba(16, 29, 64, 0.14);
  border-radius: 10px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }
`;

const ApplyDiscountButton = styled.button`
  padding: 0.65rem 1.35rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  box-shadow: 0 4px 14px rgba(157, 109, 73, 0.22);

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 18px rgba(16, 29, 64, 0.18);
    transform: translateY(-1px);
  }

  &:disabled {
    background: rgba(16, 29, 64, 0.18);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const AppliedDiscounts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const AppliedDiscount = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.55rem 0.75rem;
  background: rgba(16, 29, 64, 0.03);
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 10px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
`;

const RemoveDiscountButton = styled.button`
  background: transparent;
  color: #b71c1c;
  border: 1px solid rgba(183, 28, 28, 0.35);
  border-radius: 999px;
  padding: 0.28rem 0.65rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(183, 28, 28, 0.08);
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: clamp(1.05rem, 2.2vw, 1.15rem);
    color: ${props => props.theme.colors.primary};
    margin-top: 0.65rem;
    padding-top: 0.85rem;
    border-top: 1px solid rgba(157, 109, 73, 0.35);
  }
`;

const SummaryLabel = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: rgba(16, 29, 64, 0.55);
`;

const SummaryValue = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const UpgradeSavingsBox = styled.div`
  background: linear-gradient(
    165deg,
    rgba(232, 245, 233, 0.65) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
  border: 1px solid rgba(46, 125, 50, 0.28);
  border-radius: 16px;
  padding: 1.15rem 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: ${paymentCardShadow};
`;

const UpgradeSavingsHeader = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.02rem, 2.05vw, 1.1rem);
  margin-bottom: 0.75rem;
  color: #1b5e20;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const UpgradeSavingsAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2e7d32;
  margin: 0.5rem 0;
`;

const UpgradeSavingsText = styled.p`
  color: #388e3c;
  margin: 0.5rem 0;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const UpgradeItemsList = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(76, 175, 80, 0.3);
`;

const UpgradeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  font-size: 0.8rem;
  color: ${props => props.isUsed ? '#999' : '#555'};
  text-decoration: ${props => props.isUsed ? 'line-through' : 'none'};
  opacity: ${props => props.isUsed ? 0.6 : 1};
  transition: all 0.3s ease;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(76, 175, 80, 0.2);
  }
`;

const UpgradeItemName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UpgradeItemValue = styled.span`
  font-weight: 600;
  color: ${props => props.isUsed ? '#999' : '#2e7d32'};
`;

const UsedBadge = styled.span`
  background: rgba(46, 125, 50, 0.12);
  color: #2e7d32;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.62rem;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: 1px solid rgba(46, 125, 50, 0.28);
`;

const InfoBox = styled.div`
  background: linear-gradient(
    165deg,
    rgba(241, 239, 234, 0.55) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
  border: 1px solid rgba(16, 29, 64, 0.09);
  border-radius: 14px;
  padding: clamp(1rem, 2.5vw, 1.25rem);
  margin-top: 1.25rem;
  text-align: center;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 8px 28px rgba(16, 29, 64, 0.05);
`;

const InfoText = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.58);
  margin: 0;
  font-size: clamp(0.84rem, 1.75vw, 0.92rem);
  line-height: 1.55;
`;

const DiscountAlert = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #b71c1c;
  font-size: 0.86rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  background: rgba(255, 235, 238, 0.75);
  border: 1px solid rgba(183, 28, 28, 0.22);
  border-radius: 10px;
  line-height: 1.45;
`;

const AutoDiscountNote = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.82rem;
  color: #1b5e20;
  margin-top: 0.55rem;
  padding: 0.5rem 0.75rem;
  background: rgba(232, 245, 233, 0.65);
  border: 1px solid rgba(46, 125, 50, 0.22);
  border-radius: 10px;
  line-height: 1.45;
`;

const PaymentLoadingPanel = styled.div`
  padding: clamp(1.75rem, 4vw, 2.25rem);
  text-align: center;
  color: rgba(16, 29, 64, 0.55);
`;

const PaymentSpinner = styled.div`
  width: 28px;
  height: 28px;
  margin: 0 auto 1rem;
  border: 2px solid rgba(16, 29, 64, 0.1);
  border-top-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  animation: paySpin 0.85s linear infinite;

  @keyframes paySpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PaymentReadyPanel = styled.div`
  padding: clamp(1.5rem, 3.5vw, 2rem);
  text-align: center;
`;

const PaymentReadyTitle = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.18rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
`;

const PaymentReadyHint = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.84rem, 1.75vw, 0.9rem);
  color: rgba(16, 29, 64, 0.52);
  margin: 0 0 1.25rem;
  line-height: 1.5;
`;

const ProceedSecureButton = styled(ApplyDiscountButton)`
  padding: 0.85rem 1.65rem;
  font-size: 0.95rem;
`;

const FreePaymentNotice = styled.div`
  padding: clamp(1rem, 2.5vw, 1.35rem);
  text-align: center;
  border-radius: 14px;
  background: linear-gradient(
    165deg,
    rgba(232, 245, 233, 0.75) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
  border: 1px solid rgba(46, 125, 50, 0.22);
`;

const FreePaymentTitle = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-weight: 700;
  font-size: clamp(1rem, 2.1vw, 1.1rem);
  color: #1b5e20;
  margin-bottom: 0.35rem;
`;

const FreePaymentText = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: rgba(16, 29, 64, 0.62);
`;

const UpgradeStatusMessage = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.isSuccess ? '#1b5e20' : '#ef6c00'};
  background: ${props => props.isSuccess ? 'rgba(200, 230, 201, 0.8)' : 'rgba(255, 224, 178, 0.8)'};
  border: 1px solid ${props => props.isSuccess ? '#2e7d32' : '#fb8c00'};
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
`;

const PaymentDetails = ({ 
  totalAmount,
  customerDetails,
  basketId,
  locationId,
  onPaymentSuccess,
  onPaymentFailure,
  venposBasket,
  appliedSaverCode,
  appliedCircusCode = null,
  onBasketUpdate,
  registeredCustomerId, // Accept registered customer ID from parent
  onProceedToPayment, // If provided, render review-only mode with CTA
  autoApplyDiscountCode = null,
  membershipDetails, // Accept membership details for membership journeys
  upgradeCreditAmount, // Credit amount from original booking
  sortedUpgradeItems, // Sorted items from original booking (lowest first)
  usedUpgradeItems = { child: [], adult: [] }, // Items already used (display only)
  originalBookingData, // Full original booking data
  upgradeFromBooking, // Original booking reference
  upgradeDiscountApplied = false,
  upgradeDiscountCode = null,
  upgradeDiscountMessage = '',
  upgradeDiscountValue = 0
}) => {
  const [errors, setErrors] = useState({});
  const [paymentSession, setPaymentSession] = useState(null);
  const [initializingPayment, setInitializingPayment] = useState(false);
  const [paymentInitialized, setPaymentInitialized] = useState(false);
  const [initializationAttempted, setInitializationAttempted] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [localBasketData, setLocalBasketData] = useState(null);
  const [lastPaymentAmount, setLastPaymentAmount] = useState(null);
  const paymentMethod = 'payment_sense'; // Always Payment Sense

  // Debug: Check if we're in review-only mode
  useEffect(() => {

  }, [onProceedToPayment]);
  
  const { getSubtotal, getGroupDiscountAmount, calculateTotal, getSelectedTicketsCount, selectedTickets, selectedDate, selectedTimeSlot, pageId, modifierSelections, getFamilySaverCode } = useBooking();
  const subtotal = getSubtotal();
  const saver = getGroupDiscountAmount();
  const grandTotal = calculateTotal();
  const ticketCount = getSelectedTicketsCount();
  const isFlowerGardenShow = pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
  const isDogParkBooking = pageId === 'db0a4790-c484-474b-b350-e7ac8209f9e7';
  const familySaverLabel = isFlowerGardenShow ? 'Flower and Garden Show 2026' : 'Family Saver';
  const familySaverDiscountLabel = isFlowerGardenShow ? 'Flower and Garden Show 2026 Discount' : 'Family Saver Discount';
  const displayBasketGroupName = (name) => {
    if (isDogParkBooking && String(name || '').toLowerCase() === 'merchandise') {
      return 'Additions';
    }
    return name;
  };
  
  // Calculate applied upgrade credit client-side (1 discount per membership)
  const calculateAppliedUpgradeCredit = () => {
    if (!upgradeCreditAmount || !sortedUpgradeItems) {
      return 0;
    }
    
    // Helper functions to determine membership type
    const isChildMembership = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      
      // Exclude free child tickets (under 3, under 2, infant) - these don't count as child memberships
      if (name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
          description.includes('under 3') || description.includes('under 2') || description.includes('infant')) {
        return false;
      }
      
      return name.includes('child') || 
             name.includes('junior') || 
             description.includes('child') || 
             description.includes('junior');
    };
    const isFamilyMembership = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      return name.includes('family') || description.includes('family');
    };
    
    // Helper function to check if ticket is a free "under 3" ticket (should not count for any discounts)
    const isFreeUnder3Ticket = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      return name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
             description.includes('under 3') || description.includes('under 2') || description.includes('infant');
    };
    
    const isAdultOrConcessionItem = (item) => isAdultProductCode(item.productCode);
    
    // Get available items (handle both array format for backward compatibility and object format)
    const availableChildItems = Array.isArray(sortedUpgradeItems) 
      ? sortedUpgradeItems.filter(item => isChildProductCode(item.productCode))
      : (sortedUpgradeItems.child || []);
    
    const availableAdultItems = Array.isArray(sortedUpgradeItems)
      ? sortedUpgradeItems.filter(item => isAdultOrConcessionItem(item))
      : (sortedUpgradeItems.adult || []);
    
    // Pull already-used items if provided, to avoid double counting
    const usedChildItems = Array.isArray(usedUpgradeItems) ? [] : (usedUpgradeItems.child || []);
    const usedAdultItems = Array.isArray(usedUpgradeItems) ? [] : (usedUpgradeItems.adult || []);
    
    // Filter out used items from availability
    const remainingChildItems = availableChildItems.filter(item => !usedChildItems.includes(item));
    const remainingAdultItems = availableAdultItems.filter(item => !usedAdultItems.includes(item));
    
    if (availableChildItems.length === 0 && availableAdultItems.length === 0) {
      return 0;
    }
    
    // Separate memberships by type
    const childMemberships = selectedTickets.filter(ticket => 
      ticket.webSaleItemTypeID === 4 && isChildMembership(ticket) && !isFamilyMembership(ticket)
    );
    const adultMemberships = selectedTickets.filter(ticket => 
      ticket.webSaleItemTypeID === 4 && !isChildMembership(ticket) && !isFamilyMembership(ticket) && !isFreeUnder3Ticket(ticket)
    );
    const familyMemberships = selectedTickets.filter(ticket => 
      ticket.webSaleItemTypeID === 4 && isFamilyMembership(ticket)
    );
    
    const childMembershipCount = childMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    const adultMembershipCount = adultMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    const familyMembershipCount = familyMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    
    // Family membership can consume up to 2 adult + 3 child credits per membership
    const totalAdultNeeded = adultMembershipCount + (familyMembershipCount * 2);
    const totalChildNeeded = childMembershipCount + (familyMembershipCount * 3);
    
    if (childMembershipCount === 0 && adultMembershipCount === 0) {
      return 0;
    }
    
    // Match child items to child memberships
    const childItemsToUse = remainingChildItems.slice(0, totalChildNeeded);
    const childCredit = childItemsToUse.reduce((total, item) => {
      return total + (parseFloat(item.value) || 0);
    }, 0);
    
    // Match adult items to adult memberships
    const adultItemsToUse = remainingAdultItems.slice(0, totalAdultNeeded);
    const adultCredit = adultItemsToUse.reduce((total, item) => {
      return total + (parseFloat(item.value) || 0);
    }, 0);
    
    return childCredit + adultCredit;
  };
  
  const computedUpgradeCredit = calculateAppliedUpgradeCredit();
  
  // Check if upgrade discount code is actually in the basket
  const currentBasket = localBasketData || venposBasket;
  const basketData = currentBasket?.data ? currentBasket.data : (currentBasket || null);
  const basketDiscounts = basketData?.discounts || [];
  
  // Check if upgrade discount code exists in basket
  const upgradeDiscountInBasket = upgradeDiscountCode && basketDiscounts.some(discount => {
    const code = discount.code || discount.discountCode || discount;
    return typeof code === 'string' && (
      code.toUpperCase() === upgradeDiscountCode.toUpperCase() ||
      code.startsWith('System1-') ||
      code.startsWith('UPGRADE-')
    );
  });
  
  // Only use basket value if discount is actually in basket, otherwise use computed value
  const upgradeCreditFromBasket = (upgradeDiscountValue > 0 && upgradeDiscountInBasket) ? upgradeDiscountValue : 0;
  
  // Only show applied credit if:
  // 1. We have a computed credit amount > 0, AND
  // 2. The discount code is actually in the basket (if we have a discount code)
  const hasValidDiscount = !upgradeDiscountCode || upgradeDiscountInBasket;
  const appliedUpgradeCredit = (hasValidDiscount && computedUpgradeCredit > 0) 
    ? (upgradeCreditFromBasket > 0 ? upgradeCreditFromBasket : computedUpgradeCredit)
    : 0;
  
  // basketData already defined above
  
  const baseSubtotal = basketData?.totalPrice ?? subtotal;
  
  // Calculate total discount amount from basket discounts (excluding upgrade credits which are handled separately)
  const basketDiscountAmount = basketData?.discounts?.reduce((sum, discount) => {
    const code = (discount.code || discount.discountCode || '').toString().toUpperCase();
    // Exclude upgrade discounts as they're handled separately
    if (code.startsWith('UPGRADE-') || code.startsWith('SYSTEM1-')) {
      return sum;
    }
    return sum + (discount.discountedAmount || 0);
  }, 0) || 0;
  
  // Calculate final amount: subtotal - upgrade credit - other discounts
  const calculatedFinalAmount = Math.max(0, baseSubtotal - appliedUpgradeCredit - basketDiscountAmount);
  
  // Prefer basket finalPrice when present (including £0 after a 100% discount — do not require > 0).
  // When finalPrice is absent but we have basket data, use calculated total (subtotal − credits − discounts).
  // Without basket data yet, fall back to grandTotal so the summary isn't wrong during initial load.
  const basketFinalPrice = basketData?.finalPrice;
  const parsedBasketFinal = Number(basketFinalPrice);
  const hasExplicitBasketFinal =
    basketFinalPrice !== null &&
    basketFinalPrice !== undefined &&
    Number.isFinite(parsedBasketFinal);
  const finalAmount = hasExplicitBasketFinal
    ? Math.max(0, parsedBasketFinal)
    : basketData != null
      ? Math.max(0, calculatedFinalAmount)
      : Math.max(0, calculatedFinalAmount > 0 ? calculatedFinalAmount : grandTotal);
  
  // Debug logging to help diagnose pricing issues
  console.log('💰 [PaymentDetails] Price Calculation:', {
    basketDataTotalPrice: basketData?.totalPrice,
    basketDataFinalPrice: basketData?.finalPrice,
    subtotal,
    baseSubtotal,
    appliedUpgradeCredit,
    basketDiscountAmount,
    calculatedFinalAmount,
    grandTotal,
    finalAmount,
    hasBasketData: !!basketData,
    discounts: basketData?.discounts,
    discountCodes: basketData?.discounts?.map(d => d.code || d.discountCode)
  });
  
  // Debug paymentSession when it changes
  useEffect(() => {
    if (paymentSession) {


    }
  }, [paymentSession]);

  // Clear discount error when user starts typing a new discount code
  useEffect(() => {
    if (discountCode.trim() && errors.discount) {
      // Clear error immediately when user starts typing
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors.discount;
        return newErrors;
      });
      console.log('🧹 [PaymentDetails] Error cleared - user started typing new code');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountCode]);

  // Manual payment initialization function
  const initializePaymentSense = async () => {
    // Prevent multiple initializations
    if (!customerDetails || !basketId || !locationId || initializingPayment || paymentInitialized) {

      return;
    }

    setInitializingPayment(true);
    
    // Add a small delay to prevent ResizeObserver issues
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      // Build memberships array for membership journeys
      let membershipsArray = null;
      if (membershipDetails && Array.isArray(membershipDetails) && membershipDetails.length > 0) {
        membershipsArray = membershipDetails.map((membership, index) => {
          const memberDetail = membership.memberDetails?.[0] || {};
          return {
            membershipTypeId: membership.membershipTypeId,
            basketItemId: membership.basketItemId,
            index: membership.index || index,
            memberCount: membership.memberCount || 1,
            memberDetails: [{
              firstName: memberDetail.firstName || customerDetails?.firstName || '',
              lastName: memberDetail.lastName || customerDetails?.lastName || '',
              selectedTitle: memberDetail.selectedTitle || customerDetails?.selectedTitle || 1,
              mobile: memberDetail.mobile || customerDetails?.phone || customerDetails?.mobile || '',
              telephone: memberDetail.telephone || '',
              email: memberDetail.email || customerDetails?.email || '',
              selectedCountry: memberDetail.selectedCountry || customerDetails?.address?.country || 'GB',
              postCode: memberDetail.postCode || customerDetails?.address?.postcode || '',
              cityTown: memberDetail.cityTown || customerDetails?.address?.city || '',
              addressFirstLine: memberDetail.addressFirstLine || customerDetails?.address?.line1 || '',
              addressSecondLine: memberDetail.addressSecondLine || customerDetails?.address?.line2 || '',
              houseNameNumber: memberDetail.houseNameNumber || customerDetails?.address?.houseNumber || '',
              contactable:
                memberDetail.contactable !== undefined
                  ? Boolean(memberDetail.contactable)
                  : customerDetails?.contactable === true,
              contactableByEmail:
                memberDetail.contactableByEmail !== undefined
                  ? Boolean(memberDetail.contactableByEmail)
                  : customerDetails?.contactable === true,
              contactableBySMS: memberDetail.contactableBySMS || false,
              contactableByPost: memberDetail.contactableByPost || false,
              contactableByPhone: memberDetail.contactableByPhone || false,
              webCustomerId: registeredCustomerId || null
            }]
          };
        });
      }
      
      // Create payment request with real selected tickets and pageId for GetBasketFinalPrice
      const paymentRequest = await venposService.createPaymentRequest(
        locationId,
        basketId,
        customerDetails,
        finalAmount,
        selectedTickets,
        selectedDate,
        pageId,  // Pass real pageId instead of using basketId
        modifierSelections,  // Pass modifier selections
        registeredCustomerId,  // Pass registered customer ID from Venpos
        [], // appliedDiscounts
        null, // selectedTimeSlot
        membershipsArray // Pass memberships array
      );

      if (paymentRequest.isError) {

        setErrors({ payment: paymentRequest.error });
        setInitializingPayment(false);
        return;
      }

      // Check if this is a direct debit payment requiring redirect to GoCardless
      if (paymentRequest.data.requiresRedirect && paymentRequest.data.redirectUrl) {
        console.log('💳 [Direct Debit] Redirecting to GoCardless:', paymentRequest.data.redirectUrl);
        
        // Store payment session for when user returns from GoCardless
        const paymentSession = {
          orderId: paymentRequest.data.orderId,
          amount: finalAmount,
          currency: 'GBP',
          isDirectDebit: true,
          paymentToken: paymentRequest.data.paymentToken,
          redirectUrl: paymentRequest.data.redirectUrl
        };
        
        setPaymentSession(paymentSession);
        setLastPaymentAmount(finalAmount);
        setPaymentInitialized(true);
        setInitializingPayment(false);
        
        // Redirect to GoCardless to collect bank details
        window.location.href = paymentRequest.data.redirectUrl;
        return;
      }

      // Store payment session parameters for Payment Sense (card payments)
      const paymentSession = {
        // Common
        orderId: paymentRequest.data.orderId,
        amount: finalAmount,
        currency: 'GBP',

        // Payment Sense
        paymentSenseCheckoutUrl: paymentRequest.data.paymentSenseCheckoutUrl,
        paymentSenseJWT: paymentRequest.data.paymentSenseJWT,
        paymentSenseJWT_ALT: paymentRequest.data.paymentSenseJWT_ALT,
        paymentSenseMerchantUrl: paymentRequest.data.paymentSenseMerchantUrl,
        paymentSenseMerchantUrl_ALT: paymentRequest.data.paymentSenseMerchantUrl_ALT,
        paymentSense_ApiUrl: paymentRequest.data.paymentSense_ApiUrl
      };

      setPaymentSession(paymentSession);
      setLastPaymentAmount(finalAmount);
      setPaymentInitialized(true);
      setInitializingPayment(false);

    } catch (error) {

      setErrors({ payment: 'Failed to initialize payment. Please refresh and try again.' });
      setInitializingPayment(false);
    }
  };


  const handleApplyDiscount = async (codeOverride) => {
    const codeToApply = (typeof codeOverride === 'string' ? codeOverride : discountCode).trim();
    if (!codeToApply) {
      setErrors(prevErrors => ({ ...prevErrors, discount: 'Please enter a discount code' }));
      return;
    }
    
    if (!basketId) {
      setErrors(prevErrors => ({ ...prevErrors, discount: 'Basket not ready. Please wait a moment and try again.' }));
      console.error('Cannot apply discount: basketId is missing');
      return;
    }
    
    // Clear any previous error when attempting to apply a new discount code
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors.discount;
      return newErrors;
    });
    
    setApplyingDiscount(true);
    try {
      // CRITICAL: Always use selectedTickets to build items - it has all required fields
      // The basket data is only used for orderId, not for building items
      // This ensures we always have complete, valid items with all required fields
      const currentBasket = localBasketData || venposBasket;
      const basketData = currentBasket?.data ? currentBasket.data : (currentBasket || null);
      
      // Always build items from selectedTickets - it has all the required fields
      // The basket data might be missing fields or in a different format
      // Detect if this is a membership journey
      const isMembershipJourney = membershipDetails && membershipDetails.length > 0;
      
      // Prepare current basket items and modifiers for refresh
      // For membership journeys, skip date/time fields
      let selectedDateTime = '';
      if (!isMembershipJourney) {
        // CRITICAL: Preserve full datetime to maintain timeslot lock
        // Construct full datetime using selectedDate + selectedTimeSlot
        if (selectedDate && selectedTimeSlot) {
          const dateStr = formatDateLocal(selectedDate); // "2025-11-30"
          const timeStr = selectedTimeSlot.time || selectedTimeSlot.startDateTime?.split('T')[1] || '10:00:00';
          selectedDateTime = `${dateStr}T${timeStr}`;
        } else if (selectedDate) {
          selectedDateTime = `${formatDateLocal(selectedDate)}T00:00:00`;
        }
      }
      
      // CRITICAL FIX: Include ALL required fields for Venpos basket, including event metadata
      // Handle both main tickets AND upsells correctly (upsells have their own events!)
      // For memberships, exclude date/time fields
      const currentItems = selectedTickets
        ?.filter(ticket => ticket.quantity > 0)
        .map(ticket => {
          // Detect if this is an upsell or main ticket
          const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate';
          const isMembership = ticket.webSaleItemTypeID === 4;
          
          const baseItem = {
            pageType: ticket.pageType || (isUpsell ? 'EventTemplate' : 'WebPage'),
            pageId: ticket.pageId || (isUpsell ? ticket.parentUpsellId : pageId),
            webSaleItemId: ticket.webSaleItemId,
            webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
            quantity: ticket.quantity,
            fixedDonationAmount: 0,
            name: ticket.name || '',
            giftAidEligible: ticket.giftAidEligible || false,
            isSysGift: false,
            isSysMemberCard: false
          };
          
          // Only add date/time fields for non-membership items
          if (!isMembership && !isMembershipJourney) {
            baseItem.selectedDate = ticket.selectedDate || 
                                   ticket.selectedEventTime?.startDateTime || 
                                   selectedDateTime;
            baseItem.reservationExpiryDateTime = ticket.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString();
            baseItem.eventId = ticket.eventId || ticket.selectedEventTime?.eventId || (isUpsell ? null : selectedTimeSlot?.eventId);
            baseItem.eventCapacityUsageId = ticket.eventCapacityUsageId || ticket.selectedEventTime?.eventCapacityUsageId || (isUpsell ? null : selectedTimeSlot?.eventCapacityUsageId);
          }
          
          // Include parentWebSaleItemId for upsells
          if (ticket.parentWebSaleItemId) {
            baseItem.parentWebSaleItemId = ticket.parentWebSaleItemId;
          }
          
          // Preserve question answers and modifiers if present
          if (ticket.questionAnswers) {
            baseItem.questionAnswers = ticket.questionAnswers;
          }
          if (ticket.modifierSelection) {
            baseItem.modifierSelection = ticket.modifierSelection;
          }
          
          return baseItem;
        }) || [];

      // Get currently applied discount codes (including Family Saver if it's already applied)
      // Exclude the new discount code if it's already in the list
      const newDiscountCodeUpper = codeToApply.toUpperCase();

      // ── CHILD RETURN CODE GUARD ─────────────────────────────────────────────
      // If the entered code looks like a children's summer return code (CHILD-XXXXX-NNNN),
      // check our tracker first. If it has already been redeemed, reject it immediately
      // before even sending it to Venpos.
      if (CHILDREN_RETURN_OFFER_ENABLED && newDiscountCodeUpper.startsWith('CHILD-')) {
        try {
          const trackerBase = window.location.hostname.includes('staging')
            ? 'https://staging.belvoircastle.com/api'
            : 'https://www.belvoircastle.com/api';
          const trackerResp = await fetch(`${trackerBase}/children-return-code-tracker.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'check', discountCode: newDiscountCodeUpper }),
          });
          if (trackerResp.ok) {
            const trackerData = await trackerResp.json();
            if (trackerData.valid === false) {
              const reason = trackerData.redeemedAt
                ? 'This children\'s return code has already been used and cannot be applied again.'
                : 'This children\'s return code is not recognised.';
              setErrors(prevErrors => ({ ...prevErrors, discount: reason }));
              setApplyingDiscount(false);
              return;
            }
          }
        } catch (trackerError) {
          // Non-fatal — if tracker is unreachable, let Venpos be the final authority
          console.warn('⚠️ [ChildReturnCheck] Tracker check failed (non-fatal):', trackerError);
        }
      }
      // ── END CHILD RETURN CODE GUARD ─────────────────────────────────────────

      const currentDiscountCodes = appliedDiscounts
        .map(d => d.code)
        .filter(code => {
          if (!code) return false;
          const codeUpper = code.toUpperCase();
          // Exclude the new discount code if it's already applied
          if (codeUpper === newDiscountCodeUpper) return false;
          return true;
        });
      
      // Build discount codes array: current discounts (including Family Saver if already applied) + new discount
      // CRITICAL: Don't force Family Saver - only include it if it's already in appliedDiscounts
      // This prevents 400 errors when applying new discount codes
      const allDiscountCodes = [];
      
      // Add current discounts (including Family Saver if it's already applied)
      allDiscountCodes.push(...currentDiscountCodes);
      
      // Add the new discount code (if it's not already in the list)
      if (newDiscountCodeUpper) {
        // Check if it's already in the list (case-insensitive)
        const alreadyInList = allDiscountCodes.some(code => code.toUpperCase() === newDiscountCodeUpper);
        if (!alreadyInList) {
          allDiscountCodes.push(codeToApply);
        }
      }
      
      // Remove any duplicates (case-insensitive)
      const uniqueDiscountCodes = [];
      const seenCodes = new Set();
      for (const code of allDiscountCodes) {
        const codeUpper = code.toUpperCase();
        if (!seenCodes.has(codeUpper)) {
          seenCodes.add(codeUpper);
          uniqueDiscountCodes.push(code);
        }
      }
      
      console.log('🔍 [PaymentDetails] Building discount codes:', {
        currentDiscountCodes,
        newDiscountCode: codeToApply,
        allDiscountCodes: uniqueDiscountCodes,
        note: 'Family Saver only included if already applied, not forced'
      });
      
      // Validate we have items to refresh
      if (currentItems.length === 0) {
        setErrors({ discount: 'No items in basket to apply discount to' });
        setApplyingDiscount(false);
        return;
      }
      
      // CRITICAL: Get orderId from basket data - it's more reliable than basketId
      // This prevents 400 errors on subsequent attempts after a failed discount code
      // For the first discount code attempt, use basketId as fallback
      // Note: currentBasket and basketData are already declared above
      let actualOrderId = basketData?.orderId || basketId;
      
      console.log('🔍 [PaymentDetails] Using orderId for discount application:', {
        basketOrderId: basketData?.orderId,
        basketId,
        actualOrderId
      });
      
      // Log the items we're about to send for debugging
      console.log('📦 [PaymentDetails] Sending items to refreshBasketWithDiscounts:', {
        itemCount: currentItems.length,
        items: currentItems.map(item => ({
          webSaleItemId: item.webSaleItemId,
          name: item.name,
          quantity: item.quantity,
          selectedDate: item.selectedDate,
          eventId: item.eventId,
          eventCapacityUsageId: item.eventCapacityUsageId,
          pageType: item.pageType,
          hasSelectedDate: !!item.selectedDate,
          hasEventId: !!item.eventId,
          hasEventCapacityUsageId: !!item.eventCapacityUsageId
        })),
        discountCodes: allDiscountCodes
      });
      
      // Use the new refresh method instead of ApplyVoucher
      // Note: modifiers are NOT passed at root level - they're handled within items by Venpos
      const result = await venposService.refreshBasketWithDiscounts(
        locationId, 
        basketId, 
        currentItems, 
        uniqueDiscountCodes, // Use deduplicated discount codes
        registeredCustomerId, // Pass registered customer ID
        actualOrderId, // Pass actual orderId from basket data
        pageId // Pass pageId as fallback if items don't have it
      );
      
      // CRITICAL: Update actualOrderId from response if available (for subsequent calls)
      if (result.data?.orderId) {
        actualOrderId = result.data.orderId;
        console.log('✅ [PaymentDetails] Updated orderId from response:', actualOrderId);
      }
      
      if (result.isError) {
        console.error('❌ [PaymentDetails] Discount application error:', result.error);
        // CRITICAL: Set error FIRST before any async operations
        const errorMessage = result.error || 'Invalid discount code. Please check and try again.';
        setErrors(prevErrors => ({ ...prevErrors, discount: errorMessage }));
        console.log('🔴 [PaymentDetails] Error set in state:', errorMessage);
        
        // CRITICAL: Don't try to preserve Family Saver after error - it causes 400 errors
        // The basket should remain in a valid state for the next attempt
        // Family Saver will be automatically included if it was already applied
        console.log('ℹ️ [PaymentDetails] Skipping basket reload after error to prevent 400 errors');
      } else {
        console.log('Discount applied successfully:', result.data);
        
        // Sync appliedDiscounts state with actual basket discounts
        const actualDiscounts = result.data?.discounts || [];
        const actualDiscountCodes = actualDiscounts.map(d => {
          const code = d.code || d.discountCode || d;
          return typeof code === 'string' ? code.toUpperCase() : code;
        });
        
        // CRITICAL: Check if the discount code we tried to apply is actually in the response
        // If not, it means the discount code was invalid/rejected
        const attemptedCode = discountCode.trim().toUpperCase();
        const discountWasApplied = actualDiscountCodes.includes(attemptedCode);
        
        console.log('🔍 [PaymentDetails] Discount validation:', {
          attemptedCode,
          actualDiscountCodes,
          discountWasApplied,
          hasError: !!errors.discount
        });
        
        if (!discountWasApplied) {
          // Discount code was not applied - it's invalid
          console.error('❌ [PaymentDetails] Discount code not found in response - invalid code:', attemptedCode);
          const errorMessage = `Invalid discount code "${codeToApply}". Please check and try again.`;
          setErrors(prevErrors => ({ ...prevErrors, discount: errorMessage }));
          console.log('🔴 [PaymentDetails] Error set in state (code not applied):', errorMessage);
          
          // CRITICAL: Don't try to preserve Family Saver here - it can corrupt the basket state
          // Family Saver will be automatically included in the next attempt anyway
          // The basket response should still have Family Saver if it was there before
          // We just don't update the displayed state to show the error
          return; // Exit early - don't proceed with success flow
        }
        
        // CRITICAL: Discount was successfully applied - clear error immediately
        console.log('✅ [PaymentDetails] Discount successfully applied, clearing error');
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors.discount;
          return newErrors;
        });
        
        // CRITICAL: When a discount code is successfully applied, use whatever the API returns
        // Do NOT force Family Saver to be included - let the API decide
        // If Family Saver is in the response, it will be included; if not, it won't be
        // This allows valid discount codes to replace Family Saver if needed
        console.log('✅ [PaymentDetails] Using API response discounts (not forcing Family Saver):', actualDiscountCodes);
        
        setAppliedDiscounts(actualDiscounts.map(d => ({ code: d.code, result: d })));
        
        // CRITICAL: Clear discount code and error when discount is successfully applied
        setDiscountCode('');
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors.discount;
          console.log('✅ [PaymentDetails] Error cleared - discount successfully applied');
          return newErrors;
        });
        
        // Store the updated basket data locally to update UI totals
        setLocalBasketData(result.data);

        // Update parent component with the new basket data
        if (onBasketUpdate) {
          onBasketUpdate(result.data);
        }
      }
    } catch (error) {

      setErrors({ discount: 'Failed to apply discount code. Please try again.' });
    } finally {
      setApplyingDiscount(false);
    }
  };

  const choirAutoAppliedRef = useRef(false);
  useEffect(() => {
    if (!autoApplyDiscountCode || !basketId || choirAutoAppliedRef.current) return;
    choirAutoAppliedRef.current = true;
    setDiscountCode(autoApplyDiscountCode);
    handleApplyDiscount(autoApplyDiscountCode);
  }, [autoApplyDiscountCode, basketId]);

  const handleRemoveDiscount = async (discountCodeToRemove) => {
    try {
      // Detect if this is a membership journey
      const isMembershipJourney = membershipDetails && membershipDetails.length > 0;

      // Prepare current basket items and modifiers for refresh
      // For membership journeys, skip date/time fields
      let selectedDateTime = '';
      if (!isMembershipJourney) {
        // CRITICAL: Preserve full datetime to maintain timeslot lock
        // Construct full datetime using selectedDate + selectedTimeSlot
        if (selectedDate && selectedTimeSlot) {
          const dateStr = formatDateLocal(selectedDate); // "2025-11-30"
          const timeStr = selectedTimeSlot.time || selectedTimeSlot.startDateTime?.split('T')[1] || '10:00:00';
          selectedDateTime = `${dateStr}T${timeStr}`;
        } else if (selectedDate) {
          selectedDateTime = `${formatDateLocal(selectedDate)}T00:00:00`;
        }
      }
      
      // CRITICAL FIX: Include ALL required fields for Venpos basket, including event metadata
      // Handle both main tickets AND upsells correctly (upsells have their own events!)
      // For memberships, exclude date/time fields
      const currentItems = selectedTickets
        ?.filter(ticket => ticket.quantity > 0)
        .map(ticket => {
          // Detect if this is an upsell or main ticket
          const isUpsell = ticket.isUpsellItem || ticket.pageType === 'EventTemplate';
          const isMembership = ticket.webSaleItemTypeID === 4;
          
          const baseItem = {
            pageType: ticket.pageType || (isUpsell ? 'EventTemplate' : 'WebPage'),
            pageId: ticket.pageId || (isUpsell ? ticket.parentUpsellId : pageId),
            webSaleItemId: ticket.webSaleItemId,
            webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
            quantity: ticket.quantity,
            fixedDonationAmount: 0,
            name: ticket.name || '',
            giftAidEligible: ticket.giftAidEligible || false,
            isSysGift: false,
            isSysMemberCard: false
          };
          
          // Only add date/time fields for non-membership items
          if (!isMembership && !isMembershipJourney) {
            baseItem.selectedDate = ticket.selectedDate || 
                                   ticket.selectedEventTime?.startDateTime || 
                                   selectedDateTime;
            baseItem.reservationExpiryDateTime = ticket.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString();
            baseItem.eventId = ticket.eventId || ticket.selectedEventTime?.eventId || (isUpsell ? null : selectedTimeSlot?.eventId);
            baseItem.eventCapacityUsageId = ticket.eventCapacityUsageId || ticket.selectedEventTime?.eventCapacityUsageId || (isUpsell ? null : selectedTimeSlot?.eventCapacityUsageId);
          }
          
          // Include parentWebSaleItemId for upsells
          if (ticket.parentWebSaleItemId) {
            baseItem.parentWebSaleItemId = ticket.parentWebSaleItemId;
          }
          
          // Preserve question answers and modifiers if present
          if (ticket.questionAnswers) {
            baseItem.questionAnswers = ticket.questionAnswers;
          }
          if (ticket.modifierSelection) {
            baseItem.modifierSelection = ticket.modifierSelection;
          }
          
          return baseItem;
        }) || [];

      // Get remaining discounts (excluding the one to remove and Family Saver - we'll add it separately)
      const remainingDiscounts = appliedDiscounts
        .filter(d => d.code !== discountCodeToRemove)
        .map(d => d.code)
        .filter(code => {
          // Exclude Family Saver codes (FamilySaver2, FamilySaver3, etc.) - we'll add it separately
          return !code || (!code.startsWith('FS') && !code.startsWith('fs') && !code.startsWith('FamilySaver'));
        });
      
      // CRITICAL: Always preserve Family Saver code - get it from context
      const familySaverCode = getFamilySaverCode ? getFamilySaverCode() : appliedSaverCode;
      
      // Build discount codes array: Family Saver (if applicable) + remaining discounts
      const allDiscountCodes = [];
      if (familySaverCode) {
        allDiscountCodes.push(familySaverCode);
      }
      allDiscountCodes.push(...remainingDiscounts);
      
      // CRITICAL: Get orderId from basket data - it's more reliable than basketId
      const currentBasketForRemove = localBasketData || venposBasket;
      const basketDataForRemove = currentBasketForRemove?.data ? currentBasketForRemove.data : (currentBasketForRemove || null);
      const actualOrderIdForRemove = basketDataForRemove?.orderId || basketId;
      
      // Refresh basket without the removed discount (but with Family Saver preserved)
      // Note: modifiers are NOT passed at root level - they're handled within items by Venpos
      const result = await venposService.refreshBasketWithDiscounts(
        locationId, 
        basketId, 
        currentItems, 
        allDiscountCodes,
        registeredCustomerId, // Pass registered customer ID
        actualOrderIdForRemove // Pass actual orderId from basket data
      );
      
      if (result.isError) {

        setErrors({ discount: 'Failed to remove discount code. Please try again.' });
      } else {

        // Sync appliedDiscounts state with actual basket discounts
        const actualDiscounts = result.data?.discounts || [];
        
        // CRITICAL: Verify Family Saver is still in the response
        // If Family Saver was included in the request but not in the response, restore it
        if (familySaverCode) {
          const familySaverInResponse = actualDiscounts.some(d => {
            const code = d.code || d.discountCode || d;
            return code === familySaverCode;
          });
          
          if (!familySaverInResponse) {
            console.warn('⚠️ [PaymentDetails] Family Saver missing from basket response after removal, restoring...');
            // Family Saver was lost - restore it
            try {
              const restoreCodes = [familySaverCode, ...actualDiscounts.map(d => d.code || d.discountCode || d)];
              // Use the same orderId we used for the main request
              const restoreResult = await venposService.refreshBasketWithDiscounts(
                locationId,
                basketId,
                currentItems,
                restoreCodes,
                registeredCustomerId,
                actualOrderIdForRemove, // Use the same orderId
                pageId // Pass pageId as fallback
              );
              
              if (!restoreResult.isError) {
                // Use the restored basket data
                const restoredDiscounts = restoreResult.data?.discounts || [];
                setAppliedDiscounts(restoredDiscounts.map(d => ({ code: d.code, result: d })));
                setLocalBasketData(restoreResult.data);
                if (onBasketUpdate) {
                  onBasketUpdate(restoreResult.data);
                }
                setErrors({ discount: null });
                return; // Exit early since we've restored
              }
            } catch (restoreError) {
              console.error('Failed to restore Family Saver after discount removal:', restoreError);
              // Continue with the original response but log the issue
            }
          }
        }
        
        setAppliedDiscounts(actualDiscounts.map(d => ({ code: d.code, result: d })));
        
        setErrors({ discount: null });
        
        // Store the updated basket data locally to update UI totals
        setLocalBasketData(result.data);

        // Update parent component with the new basket data
        if (onBasketUpdate) {
          onBasketUpdate(result.data);
        }
      }
    } catch (error) {

      setErrors({ discount: 'Failed to remove discount code. Please try again.' });
    }
  };

  const handlePaymentSenseSuccess = (paymentResult) => {

    if (onPaymentSuccess) {
      onPaymentSuccess(paymentResult);
    }
  };

  const handlePaymentSenseFailure = (error) => {

    setErrors({ payment: error.error || 'Payment failed. Please try again.' });
    if (onPaymentFailure) {
      onPaymentFailure(error);
    }
  };

  const handlePaymentSenseCancel = () => {

    setErrors({ payment: 'Payment was cancelled. You can try again.' });
  };


  return (
    <PaymentContainer>
      <PageHeader>
        <PageHeaderAccent aria-hidden />
        <SectionEyebrow>Checkout</SectionEyebrow>
        <SectionTitle>Payment details</SectionTitle>
        <SectionSubtitle>
          Review your order, apply a code if you have one, then complete secure payment.
        </SectionSubtitle>
      </PageHeader>

      {/* Discount Code Section */}
      <DiscountSection>
        <DiscountHeader>Discount codes</DiscountHeader>
        
        <DiscountInputContainer>
          <DiscountInput
            type="text"
            placeholder="Enter discount code"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
            disabled={applyingDiscount}
          />
          <ApplyDiscountButton
            onClick={handleApplyDiscount}
            disabled={!discountCode.trim() || applyingDiscount}
          >
            {applyingDiscount ? 'Applying...' : 'Apply'}
          </ApplyDiscountButton>
        </DiscountInputContainer>

        {errors.discount && (
          <DiscountAlert role="alert">{errors.discount}</DiscountAlert>
        )}

        {appliedDiscounts.length > 0 && (
          <AppliedDiscounts>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Applied Discounts:
            </div>
            {appliedDiscounts
              .filter(discount => {
                // Always hide Family Saver codes (FamilySaver2, FamilySaver3, etc.) from the UI
                const code = (discount.code || '').toString().toUpperCase();
                return !code.startsWith('FS') && !code.startsWith('FAMILYSAVER');
              })
              .map((discount, index) => (
                <AppliedDiscount key={index}>
                  <span>{discount.code}</span>
                  <RemoveDiscountButton onClick={() => handleRemoveDiscount(discount.code)}>
                    Remove
                  </RemoveDiscountButton>
                </AppliedDiscount>
              ))}
          </AppliedDiscounts>
        )}

        {/* Family Saver — only when basket actually includes FS discount */}
        {!errors.discount && basketDiscounts.some(d => {
          const code = (d.code || d.discountCode || '').toString().toUpperCase();
          return code.startsWith('FS') || code.startsWith('FAMILYSAVER');
        }) && (
          <AutoDiscountNote>{familySaverLabel} discount applied automatically</AutoDiscountNote>
        )}

        {/* Circus bundle discount */}
        {!errors.discount && basketDiscounts.some(d => {
          const code = (d.code || d.discountCode || '').toString().toUpperCase();
          return code.startsWith('CIRCUS-');
        }) && (
          <AutoDiscountNote>20% discount applied automatically to entry tickets</AutoDiscountNote>
        )}
      </DiscountSection>

      {/* Payment section - only show if NOT in review mode */}
      {!onProceedToPayment && paymentMethod === 'payment_sense' && finalAmount > 0 && (
        <PaymentSection style={{ padding: '0', background: 'transparent', border: 'none', boxShadow: 'none' }}>
          {errors.payment && (
            <ErrorMessage style={{ marginBottom: '1rem', padding: '1rem', borderRadius: '8px', background: '#f8f9fa' }}>
              {errors.payment}
            </ErrorMessage>
          )}

          {initializingPayment && (
            <PaymentLoadingPanel>
              <PaymentSpinner aria-hidden />
              <div style={{ fontFamily: 'system-ui, sans-serif', fontSize: '0.92rem', fontWeight: 600, color: 'rgba(16,29,64,0.72)', marginBottom: '0.35rem' }}>
                Initializing secure payment
              </div>
              <div style={{ fontFamily: 'system-ui, sans-serif', fontSize: '0.84rem', color: 'rgba(16,29,64,0.48)' }}>
                Please wait while we set up your payment session.
              </div>
            </PaymentLoadingPanel>
          )}

          {paymentSession && !initializingPayment && (
            <>
              <PaymentSenseConnectE
                paymentSession={paymentSession}
                onPaymentSuccess={handlePaymentSenseSuccess}
                onPaymentFailure={handlePaymentSenseFailure}
                onPaymentCancel={handlePaymentSenseCancel}
              />
              
              <PaymentSenseWallet
                paymentSession={paymentSession}
                customerDetails={customerDetails}
                onPaymentSuccess={handlePaymentSenseSuccess}
                onPaymentFailure={handlePaymentSenseFailure}
                onPaymentCancel={handlePaymentSenseCancel}
              />
            </>
          )}

          {!paymentSession && !initializingPayment && !errors.payment && (
            <PaymentReadyPanel>
              <PaymentReadyTitle>Ready to pay?</PaymentReadyTitle>
              <PaymentReadyHint>
                If you have a discount code, apply it in the section above before continuing.
              </PaymentReadyHint>
              <ProceedSecureButton
                type="button"
                onClick={initializePaymentSense}
                disabled={!customerDetails || !basketId || !locationId}
              >
                Proceed to secure payment
              </ProceedSecureButton>
            </PaymentReadyPanel>
          )}
        </PaymentSection>
      )}

      {/* No payment required (free/fully discounted) */}
      {paymentMethod === 'payment_sense' && finalAmount <= 0 && (
        <PaymentSection style={{ padding: '0', background: 'transparent', border: 'none', boxShadow: 'none' }}>
          <FreePaymentNotice>
            <FreePaymentTitle>No payment required</FreePaymentTitle>
            <FreePaymentText>Your total is £{finalAmount.toFixed(2)} after discounts.</FreePaymentText>
          </FreePaymentNotice>
        </PaymentSection>
      )}


      {basketData?.groups && basketData.groups.length > 0 ? (
        <ItemsSection>
          <ItemsHeader>Your Tickets</ItemsHeader>
          {basketData.groups.map((grp, gi) => {
            // Group items by name + modifier to combine duplicates
            const groupedItems = (grp.items || []).reduce((acc, it) => {
              const modifierKey = it.modifierSelection?.modifierName || 'no-modifier';
              const key = `${it.name}::${modifierKey}`;
              
              if (!acc[key]) {
                acc[key] = {
                  ...it,
                  quantity: Number(it.quantity || 0),
                  totalPrice: typeof it.discountedAmount === 'number' ? it.discountedAmount : 
                             (typeof it.totalPrice === 'number' ? it.totalPrice : (Number(it.price || 0) * Number(it.quantity || 0)))
                };
              } else {
                // Combine quantities and prices
                acc[key].quantity += Number(it.quantity || 0);
                const itemPrice = typeof it.discountedAmount === 'number' ? it.discountedAmount : 
                                 (typeof it.totalPrice === 'number' ? it.totalPrice : (Number(it.price || 0) * Number(it.quantity || 0)));
                acc[key].totalPrice += itemPrice;
              }
              
              return acc;
            }, {});
            
            return (
              <div key={gi}>
                {grp?.name && <div style={{ fontWeight: 600, margin: '0.25rem 0 0.5rem' }}>{displayBasketGroupName(grp.name)}</div>}
                {Object.values(groupedItems).map((it, ii) => {
                  // Get modifier information from the ticket itself
                  const modifierText = (it.modifierSelection && it.modifierSelection.modifierName) 
                    ? ` • ${it.modifierSelection.modifierName}` 
                    : '';
                  
                  return (
                    <ItemRow key={ii}>
                      <ItemName>
                        {it.name}
                        {modifierText && <ModifierInfo>{modifierText}</ModifierInfo>}
                      </ItemName>
                      <ItemQty>x{it.quantity}</ItemQty>
                      <ItemTotal>£{it.totalPrice.toFixed(2)}</ItemTotal>
                    </ItemRow>
                  );
                })}
              </div>
            );
          })}
        </ItemsSection>
      ) : venposBasket?.data?.groups && venposBasket.data.groups.length > 0 ? (
        <ItemsSection>
          <ItemsHeader>Your Tickets</ItemsHeader>
          {venposBasket.data.groups.map((grp, gi) => {
            // Group items by name + modifier to combine duplicates
            const groupedItems = (grp.items || []).reduce((acc, it) => {
              const modifierKey = it.modifierSelection?.modifierName || 'no-modifier';
              const key = `${it.name}::${modifierKey}`;
              
              if (!acc[key]) {
                acc[key] = {
                  ...it,
                  quantity: Number(it.quantity || 0),
                  totalPrice: typeof it.discountedAmount === 'number' ? it.discountedAmount : 
                             (typeof it.totalPrice === 'number' ? it.totalPrice : (Number(it.price || 0) * Number(it.quantity || 0)))
                };
              } else {
                // Combine quantities and prices
                acc[key].quantity += Number(it.quantity || 0);
                const itemPrice = typeof it.discountedAmount === 'number' ? it.discountedAmount : 
                                 (typeof it.totalPrice === 'number' ? it.totalPrice : (Number(it.price || 0) * Number(it.quantity || 0)));
                acc[key].totalPrice += itemPrice;
              }
              
              return acc;
            }, {});
            
            return (
              <div key={gi}>
                {grp?.name && <div style={{ fontWeight: 600, margin: '0.25rem 0 0.5rem' }}>{displayBasketGroupName(grp.name)}</div>}
                {Object.values(groupedItems).map((it, ii) => {
                  // Get modifier information from the ticket itself
                  const modifierText = (it.modifierSelection && it.modifierSelection.modifierName) 
                    ? ` • ${it.modifierSelection.modifierName}` 
                    : '';
                  
                  return (
                    <ItemRow key={ii}>
                      <ItemName>
                        {it.name}
                        {modifierText && <ModifierInfo>{modifierText}</ModifierInfo>}
                      </ItemName>
                      <ItemQty>x{it.quantity}</ItemQty>
                      <ItemTotal>£{it.totalPrice.toFixed(2)}</ItemTotal>
                    </ItemRow>
                  );
                })}
              </div>
            );
          })}
        </ItemsSection>
      ) : selectedTickets && selectedTickets.length > 0 ? (
        // Fallback: Show selected tickets if basket isn't loaded yet
        <ItemsSection>
          <ItemsHeader>Your {membershipDetails ? 'Membership' : 'Tickets'}</ItemsHeader>
          {selectedTickets
            .filter(ticket => ticket.quantity > 0)
            .map((ticket, index) => (
              <ItemRow key={index}>
                <ItemName>{ticket.name}</ItemName>
                <ItemQty>x{ticket.quantity}</ItemQty>
                <ItemTotal>£{((ticket.price || 0) * ticket.quantity).toFixed(2)}</ItemTotal>
              </ItemRow>
            ))}
        </ItemsSection>
      ) : (
        <ItemsSection>
          <ItemsHeader>Your Tickets</ItemsHeader>
          <div style={{ padding: '1rem', fontStyle: 'italic', color: '#666' }}>
            Loading basket items...
          </div>
        </ItemsSection>
      )}

      {/* Upgrade Savings Display */}
      {upgradeCreditAmount > 0 && (() => {
        // Helper functions to determine membership type (same as in calculateAppliedUpgradeCredit)
        const isChildMembership = (ticket) => {
          const name = (ticket.name || '').toLowerCase();
          const description = (ticket.description || '').toLowerCase();
          if (name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
              description.includes('under 3') || description.includes('under 2') || description.includes('infant')) {
            return false;
          }
          return name.includes('child') || 
                 name.includes('junior') || 
                 description.includes('child') || 
                 description.includes('junior');
        };
        const isFamilyMembership = (ticket) => {
          const name = (ticket.name || '').toLowerCase();
          const description = (ticket.description || '').toLowerCase();
          return name.includes('family') || description.includes('family');
        };
        const isFreeUnder3Ticket = (ticket) => {
          const name = (ticket.name || '').toLowerCase();
          const description = (ticket.description || '').toLowerCase();
          return name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
                 description.includes('under 3') || description.includes('under 2') || description.includes('infant');
        };
        
        // Calculate memberships that will actually use credits (excluding "under 3" free memberships)
        const childMemberships = selectedTickets.filter(ticket => 
          ticket.webSaleItemTypeID === 4 && isChildMembership(ticket) && !isFamilyMembership(ticket)
        );
        const adultMemberships = selectedTickets.filter(ticket => 
          ticket.webSaleItemTypeID === 4 && !isChildMembership(ticket) && !isFamilyMembership(ticket) && !isFreeUnder3Ticket(ticket)
        );
        const familyMemberships = selectedTickets.filter(ticket =>
          ticket.webSaleItemTypeID === 4 && isFamilyMembership(ticket)
        );
        
        const childMembershipCount = childMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
        const adultMembershipCount = adultMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
        const familyMembershipCount = familyMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
        
        // Family membership can consume up to 2 adult + 3 child credits per membership
        const totalAdultNeeded = adultMembershipCount + (familyMembershipCount * 2);
        const totalChildNeeded = childMembershipCount + (familyMembershipCount * 3);
        const totalMembershipsUsingCredits = childMembershipCount + adultMembershipCount + familyMembershipCount;
        
        // Get available items to determine how many are actually used
        const availableChildItems = Array.isArray(sortedUpgradeItems) 
          ? sortedUpgradeItems.filter(item => isChildProductCode(item.productCode))
          : (sortedUpgradeItems?.child || []);
        const availableAdultItems = Array.isArray(sortedUpgradeItems)
          ? sortedUpgradeItems.filter(item => isAdultProductCode(item.productCode))
          : (sortedUpgradeItems?.adult || []);
        
        // Calculate how many items are actually being used
        const childItemsToUse = availableChildItems.slice(0, totalChildNeeded);
        const adultItemsToUse = availableAdultItems.slice(0, totalAdultNeeded);
        const totalItemsUsed = childItemsToUse.length + adultItemsToUse.length;
        
        console.log('💳 [PaymentDetails] Credits calculation:', {
          childMembershipCount,
          adultMembershipCount,
          familyMembershipCount,
          totalChildNeeded,
          totalAdultNeeded,
          availableChildCount: availableChildItems.length,
          availableAdultCount: availableAdultItems.length,
          childItemsToUseCount: childItemsToUse.length,
          adultItemsToUseCount: adultItemsToUse.length,
          totalItemsUsed
        });
        
        // appliedUpgradeCredit is already calculated above using the same matching logic as TicketSelection
        // It checks both the computed credit AND if the discount is in the basket
        
        return (
          <UpgradeSavingsBox>
            <UpgradeSavingsHeader>
              Upgrade credit
            </UpgradeSavingsHeader>
            <UpgradeSavingsAmount>
              £{upgradeCreditAmount.toFixed(2)} Available
              {appliedUpgradeCredit > 0 && (
                <span style={{ fontSize: '0.9rem', fontWeight: 400, marginLeft: '0.5rem', display: 'block', marginTop: '0.25rem' }}>
                  £{appliedUpgradeCredit.toFixed(2)} applied ({totalMembershipsUsingCredits} membership{totalMembershipsUsingCredits !== 1 ? 's' : ''})
                </span>
              )}
            </UpgradeSavingsAmount>
            <UpgradeSavingsText>
              Your original booking value will be credited towards this membership.
              {upgradeFromBooking && (
                <span style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8rem' }}>
                  Booking: {upgradeFromBooking}
                </span>
              )}
            </UpgradeSavingsText>
            {upgradeDiscountMessage && (
              <UpgradeStatusMessage isSuccess={upgradeDiscountApplied}>
                {upgradeDiscountMessage}
              </UpgradeStatusMessage>
            )}
            {(() => {
              const availableItems = Array.isArray(sortedUpgradeItems)
                ? sortedUpgradeItems
                : ([...(sortedUpgradeItems?.child || []), ...(sortedUpgradeItems?.adult || [])]);
              const totalAvailable = availableItems.length;
              
              const usedChildItems = Array.isArray(usedUpgradeItems) ? [] : (usedUpgradeItems.child || []);
              const usedAdultItems = Array.isArray(usedUpgradeItems) ? [] : (usedUpgradeItems.adult || []);
              const totalPreviouslyUsed = usedChildItems.length + usedAdultItems.length;
              
              const totalItems = availableChildItems.length + availableAdultItems.length;
              
              console.log('💳 [PaymentDetails] Upgrade Credits Display:', {
                totalChildNeeded,
                totalAdultNeeded,
                availableChildItemsCount: availableChildItems.length,
                availableAdultItemsCount: availableAdultItems.length,
                childItemsToUseCount: childItemsToUse.length,
                adultItemsToUseCount: adultItemsToUse.length,
                totalItemsUsed
              });
              
              return (
                <>
                  {totalItems > 0 && (
                    <UpgradeItemsList>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.4rem', fontWeight: 600 }}>
                        Available Credits ({totalItemsUsed} of {totalItems} used):
                      </div>
                      
                      {/* Child Items Section */}
                      {availableChildItems.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.5rem', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase' }}>
                            Child Items (for Child Memberships only)
                          </div>
                          {availableChildItems.map((item, index) => {
                            const isUsed = index < totalChildNeeded;
                            return (
                              <UpgradeItem key={`child-${index}`} isUsed={isUsed}>
                                <UpgradeItemName>
                                  {isUsed && <UsedBadge>Used</UsedBadge>}
                                  {item.productDescription || item.productCode}
                                  <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Child)</span>
                                </UpgradeItemName>
                                <UpgradeItemValue isUsed={isUsed}>
                                  £{parseFloat(item.value).toFixed(2)}
                                </UpgradeItemValue>
                              </UpgradeItem>
                            );
                          })}
                        </>
                      )}
                      
                      {/* Adult Items Section */}
                      {availableAdultItems.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: availableChildItems.length > 0 ? '0.5rem' : '0', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase' }}>
                            Adult Items (for Adult Memberships only)
                          </div>
                          {availableAdultItems.map((item, index) => {
                            const isUsed = index < totalAdultNeeded;
                            return (
                              <UpgradeItem key={`adult-${index}`} isUsed={isUsed}>
                                <UpgradeItemName>
                                  {isUsed && <UsedBadge>Used</UsedBadge>}
                                  {item.productDescription || item.productCode}
                                  <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Adult)</span>
                                </UpgradeItemName>
                                <UpgradeItemValue isUsed={isUsed}>
                                  £{parseFloat(item.value).toFixed(2)}
                                </UpgradeItemValue>
                              </UpgradeItem>
                            );
                          })}
                        </>
                      )}
                      
                      {totalItemsUsed === 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem', fontStyle: 'italic', textAlign: 'center' }}>
                          Add memberships to use these credits
                        </div>
                      )}
                    </UpgradeItemsList>
                  )}
                  
                  {totalPreviouslyUsed > 0 && (
                    <UpgradeItemsList>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.3rem', fontWeight: 600 }}>
                        Already used credits (not available): {totalPreviouslyUsed}
                      </div>
                      
                      {usedChildItems.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.1rem', marginBottom: '0.2rem', fontWeight: 600, textTransform: 'uppercase' }}>
                            Child Items
                          </div>
                          {usedChildItems.map((item, index) => (
                            <UpgradeItem key={`used-child-${index}`} isUsed>
                              <UpgradeItemName>
                                <UsedBadge>Used</UsedBadge>
                                {item.productDescription || item.productCode}
                                <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Child)</span>
                              </UpgradeItemName>
                              <UpgradeItemValue isUsed>
                                £{parseFloat(item.value).toFixed(2)}
                              </UpgradeItemValue>
                            </UpgradeItem>
                          ))}
                        </>
                      )}
                      
                      {usedAdultItems.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: usedChildItems.length > 0 ? '0.35rem' : '0.1rem', marginBottom: '0.2rem', fontWeight: 600, textTransform: 'uppercase' }}>
                            Adult Items
                          </div>
                          {usedAdultItems.map((item, index) => (
                            <UpgradeItem key={`used-adult-${index}`} isUsed>
                              <UpgradeItemName>
                                <UsedBadge>Used</UsedBadge>
                                {item.productDescription || item.productCode}
                                <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Adult)</span>
                              </UpgradeItemName>
                              <UpgradeItemValue isUsed>
                                £{parseFloat(item.value).toFixed(2)}
                              </UpgradeItemValue>
                            </UpgradeItem>
                          ))}
                        </>
                      )}
                    </UpgradeItemsList>
                  )}
                </>
              );
            })()}
          </UpgradeSavingsBox>
        );
      })()}

      <OrderSummary>
        <SectionHeader>
          Total ({ticketCount} ticket{ticketCount !== 1 ? 's' : ''})
        </SectionHeader>
        
        {basketData ? (
          <>
            <SummaryRow>
              <SummaryLabel>Subtotal</SummaryLabel>
              <SummaryValue>£{baseSubtotal.toFixed(2)}</SummaryValue>
            </SummaryRow>
            {/* Show upgrade credit as a single line item if applied */}
            {appliedUpgradeCredit > 0 && (
              <SummaryRow>
                <SummaryLabel>Upgrade Credit</SummaryLabel>
                <SummaryValue>-£{appliedUpgradeCredit.toFixed(2)}</SummaryValue>
              </SummaryRow>
            )}
            {/* Show other discounts from basket (Family Saver, etc.) */}
            {/* Don't show discounts if there's a discount error - prevents showing Family Saver when discount code fails */}
            {basketData.discounts && basketData.discounts.length > 0 && !errors.discount && (
              <>
                {basketData.discounts
                  .filter(discount => {
                    const code = discount.code ? discount.code.toUpperCase() : '';
                    // Always exclude Family Saver codes (FamilySaver2, FamilySaver3, etc.) from UI
                    // Exclude Circus codes (CIRCUS-XXXXXX-0400) - show friendly message instead
                    // Exclude upgrade discounts (UPGRADE- and System1-) as we show them separately above as "Upgrade Credit"
                    return !code.startsWith('FS') && !code.startsWith('FAMILYSAVER') && !code.startsWith('CIRCUS-') && !code.startsWith('UPGRADE-') && !code.startsWith('SYSTEM1-');
                  })
                  .map((discount, index) => {
                    const code = discount.code ? discount.code.toUpperCase() : '';
                    let discountLabel;
                    
                    if (code.startsWith('FS') || code.startsWith('FAMILYSAVER')) {
                      discountLabel = familySaverDiscountLabel;
                    } else if (code.startsWith('TZ')) {
                      discountLabel = 'Travel Zoo Discount';
                    } else {
                      discountLabel = discount.code;
                    }
                    
                    return (
                      <SummaryRow key={index}>
                        <SummaryLabel>{discountLabel}</SummaryLabel>
                        <SummaryValue>-£{(discount.discountedAmount || 0).toFixed(2)}</SummaryValue>
                      </SummaryRow>
                    );
                  })}
              </>
            )}
            <SummaryRow>
              <SummaryLabel>Today</SummaryLabel>
              <SummaryValue>£{finalAmount.toFixed(2)}</SummaryValue>
            </SummaryRow>
          </>
        ) : venposBasket?.data ? (
          <>
            <SummaryRow>
              <SummaryLabel>Subtotal</SummaryLabel>
              <SummaryValue>£{(venposBasket.data.totalPrice || subtotal).toFixed(2)}</SummaryValue>
            </SummaryRow>
            {/* Show upgrade credit as a single line item if applied */}
            {appliedUpgradeCredit > 0 && (
              <SummaryRow>
                <SummaryLabel>Upgrade Credit</SummaryLabel>
                <SummaryValue>-£{appliedUpgradeCredit.toFixed(2)}</SummaryValue>
              </SummaryRow>
            )}
            {/* Show other discounts from basket (Family Saver, etc.) */}
            {/* Don't show discounts if there's a discount error - prevents showing Family Saver when discount code fails */}
            {venposBasket.data.discounts && venposBasket.data.discounts.length > 0 && !errors.discount && (
              <>
                {venposBasket.data.discounts
                  .filter(discount => {
                    const code = (discount.code || discount.discountCode || '').toString().toUpperCase();
                    // Always exclude Family Saver codes (FamilySaver2, FamilySaver3, etc.) from UI
                    // Exclude Circus codes (CIRCUS-XXXXXX-0400) - show friendly message instead
                    // Exclude upgrade discounts (UPGRADE- and System1-) as we show them separately above as "Upgrade Credit"
                    return !code.startsWith('FS') && !code.startsWith('FAMILYSAVER') && !code.startsWith('CIRCUS-') && !code.startsWith('UPGRADE-') && !code.startsWith('SYSTEM1-');
                  })
                  .map((discount, index) => {
                    const code = (discount.code || discount.discountCode || '').toString().toUpperCase();
                    let discountLabel;
                    
                    console.log('🎟️ [PaymentDetails] Processing discount:', { 
                      originalCode: discount.code, 
                      discountCode: discount.discountCode,
                      processedCode: code,
                      discountedAmount: discount.discountedAmount,
                      fullDiscount: discount
                    });
                    
                    if (code.startsWith('FS') || code.startsWith('FAMILYSAVER')) {
                      discountLabel = familySaverDiscountLabel;
                    } else if (code.startsWith('TZ')) {
                      discountLabel = 'Travel Zoo Discount';
                    } else {
                      discountLabel = discount.code || discount.discountCode || 'Discount';
                    }
                    
                    return (
                      <SummaryRow key={index}>
                        <SummaryLabel>{discountLabel}</SummaryLabel>
                        <SummaryValue>-£{(discount.discountedAmount || 0).toFixed(2)}</SummaryValue>
                      </SummaryRow>
                    );
                  })}
              </>
            )}
            <SummaryRow>
              <SummaryLabel>Today</SummaryLabel>
              <SummaryValue>£{finalAmount.toFixed(2)}</SummaryValue>
            </SummaryRow>
          </>
        ) : (
          <>
            <SummaryRow>
              <SummaryLabel>Subtotal</SummaryLabel>
              <SummaryValue>£{subtotal.toFixed(2)}</SummaryValue>
            </SummaryRow>
            {appliedUpgradeCredit > 0 && (
              <SummaryRow>
                <SummaryLabel>Upgrade Credit</SummaryLabel>
                <SummaryValue>-£{appliedUpgradeCredit.toFixed(2)}</SummaryValue>
              </SummaryRow>
            )}
            {saver > 0 && (
              <SummaryRow>
              <SummaryLabel>{familySaverLabel}</SummaryLabel>
                <SummaryValue>-£{saver.toFixed(2)}</SummaryValue>
              </SummaryRow>
            )}
            <SummaryRow>
              <SummaryLabel>Today</SummaryLabel>
              <SummaryValue>£{Math.max(0, grandTotal - appliedUpgradeCredit).toFixed(2)}</SummaryValue>
            </SummaryRow>
          </>
        )}
      </OrderSummary>

      <InfoBox>
        <InfoText>
          Your payment is processed securely. We accept major credit and debit cards.
          Your card details are encrypted and are not stored on our servers.
        </InfoText>
      </InfoBox>
    </PaymentContainer>
  );
};

export default PaymentDetails;